
/**
 * Service to handle visit scheduling requests.
 * Implements GET requests with payload as query parameters as per requirements.
 */

export const submitVisitRequest = async (url: string, payload: any) => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      // Using text/plain to avoid preflight OPTIONS request which can be tricky with Google Apps Script
      // while still sending valid JSON in the body.
      headers: {
        'Content-Type': 'text/plain;charset=utf-8',
      },
      body: JSON.stringify(payload),
    });

    // 1. Check HTTP response status (200-299)
    if (!response.ok) {
      throw new Error(`Error de red (HTTP ${response.status}): ${response.statusText}`);
    }

    const result = await response.json();

    // 2. Verification of internal status: must be in the 20X range (200-209)
    // We check both number and string types for flexibility
    const status = result?.status;
    const isSuccess = (typeof status === 'number' && status >= 200 && status <= 209) || 
                      (typeof status === 'string' && status.startsWith('20'));

    if (!isSuccess) {
      const serverMessage = result?.message || result?.error || `Código de estado inesperado: ${status}`;
      throw new Error(`La operación fue rechazada por el servidor: ${serverMessage}`);
    }

    return result;
  } catch (error) {
    // Log the full error for debugging but rethrow for the UI to handle
    console.error("Error detallado en submitVisitRequest:", error);
    throw error;
  }
};

/**
 * Specific service for Broker and Broker Premium visit requests.
 * Handles Zoho integration and response validation.
 */
export const submitBrokerVisitRequest = async (payload: any) => {
  const BROKER_WEBHOOK_URL = "https://script.google.com/macros/s/AKfycbyPR-OORSgN-iCvwcvYCXl1r04MAnNHpH-xfghT-5jyFm53IoK7WufUTgXrdnBDMKiyhg/exec";
  
  try {
    const response = await fetch(BROKER_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain;charset=utf-8',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Error de red (HTTP ${response.status})`);
    }

    const result = await response.json();
    const status = result?.status;

    // Expected status 200 or 201
    if (status === 200 || status === 201) {
      return result;
    } else {
      // If error, we throw an object containing the message and requestId
      const errorData = {
        message: result?.message || "Error desconocido en el servidor",
        requestId: result?.requestId || "N/A",
        status: status
      };
      throw errorData;
    }
  } catch (error: any) {
    console.error("Error en submitBrokerVisitRequest:", error);
    throw error;
  }
};

/**
 * Crea una tarea de visita en Zoho CRM mediante nuestro backend.
 */
export const createZohoVisitTask = async (payload: any) => {
  try {
    const response = await fetch('/api/zoho/visit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: payload }),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || `Error de servidor (HTTP ${response.status})`);
    }

    return await response.json();
  } catch (error: any) {
    console.error("Error en createZohoVisitTask:", error);
    throw error;
  }
};

/**
 * Agendamiento de visita desde cliente procesado por el backend
 */
export const submitClientVisitBackendRequest = async (payload: any) => {
  try {
    const response = await fetch('/api/client-visit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ request: payload }),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || `Error del servidor (${response.status})`);
    }

    return await response.json();
  } catch (error: any) {
    console.error("Error en submitClientVisitBackendRequest:", error);
    throw error;
  }
};


